 
import { useState, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBeta } from '../../../contexts/BetaContext.jsx';
import {
  FAQ_CATEGORIES,
  getFeatureById,
  getContextHelpForPath,
  getFaqOpenPath,
  resolveFeatureOpenPath,
  resolveOpenPath,
  searchFeatures,
  searchFaq,
} from '../../../help/portalFeatureMap.js';
import HubHelpModalShell from './HubHelpModalShell.jsx';

const SEVERITY_LABEL = {
  high: { label: '重要', className: 'hub-help-severity--high' },
  medium: { label: '中', className: 'hub-help-severity--medium' },
  low: { label: '低', className: 'hub-help-severity--low' },
};

function CategoryIcon({ type }) {
  const props = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, 'aria-hidden': true };
  switch (type) {
    case 'key':
      return (
        <svg {...props}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
        </svg>
      );
    case 'layout':
      return (
        <svg {...props}>
          <rect x="3" y="3" width="7" height="9" />
          <rect x="14" y="3" width="7" height="5" />
          <rect x="14" y="12" width="7" height="9" />
          <rect x="3" y="16" width="7" height="5" />
        </svg>
      );
    case 'grid':
      return (
        <svg {...props}>
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...props}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    case 'credit-card':
      return (
        <svg {...props}>
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
          <line x1="1" y1="10" x2="23" y2="10" />
        </svg>
      );
    case 'users':
      return (
        <svg {...props}>
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
      );
    case 'package':
      return (
        <svg {...props}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path strokeLinecap="round" d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01" />
        </svg>
      );
  }
}

export default function HubHelpModal({ isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { effectiveIsBeta, userHubNavVisibility } = useBeta();
  const betaOpts = useMemo(
    () => ({ effectiveIsBeta, userHubNavVisibility }),
    [effectiveIsBeta, userHubNavVisibility]
  );
  const [activeTab, setActiveTab] = useState('faq');
  const [searchQuery, setSearchQuery] = useState('');
  const [faqCategory, setFaqCategory] = useState('');
  const [expandedFeature, setExpandedFeature] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const contentRef = useRef(null);
  const itemRefs = useRef(new Map());

  const contextHelp = useMemo(
    () => getContextHelpForPath(location.pathname),
    [location.pathname]
  );

  const filteredCategories = useMemo(
    () => searchFeatures(searchQuery),
    [searchQuery]
  );

  const filteredFaq = useMemo(
    () => searchFaq(searchQuery, faqCategory || null),
    [searchQuery, faqCategory]
  );

  const hasContextHelp = contextHelp.features.length > 0 || contextHelp.faqs.length > 0;
  const showContextSection = hasContextHelp && !searchQuery.trim();

  const resetState = useCallback(() => {
    setSearchQuery('');
    setFaqCategory('');
    setExpandedFeature(null);
    setExpandedFaq(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleOpenFeature = useCallback((openPath) => {
    if (!openPath) return;
    handleClose();

    const hashIndex = openPath.indexOf('#');
    if (hashIndex !== -1) {
      const pathname = openPath.slice(0, hashIndex);
      const hash = openPath.slice(hashIndex + 1);
      navigate(pathname);
      window.setTimeout(() => {
        const el = document.getElementById(hash)
          || document.querySelector(`[data-section="${hash}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
      return;
    }

    navigate(openPath);
  }, [handleClose, navigate]);

  const scrollToItem = useCallback((id) => {
    requestAnimationFrame(() => {
      const el = itemRefs.current.get(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }, []);

  const openFeature = useCallback((featureId) => {
    setActiveTab('map');
    setExpandedFeature(featureId);
    setExpandedFaq(null);
    scrollToItem(`feature-${featureId}`);
  }, [scrollToItem]);

  const openFaq = useCallback((faqId) => {
    setActiveTab('faq');
    setExpandedFaq(faqId);
    setExpandedFeature(null);
    scrollToItem(`faq-${faqId}`);
  }, [scrollToItem]);

  const setItemRef = useCallback((key, el) => {
    if (el) itemRefs.current.set(key, el);
    else itemRefs.current.delete(key);
  }, []);

  const renderFeatureOpenActions = (feature) => {
    const actions = [
      feature.openPath && {
        path: resolveOpenPath(feature.openPath, betaOpts),
        label: feature.openPathLabel || '開く',
        primary: true,
      },
      feature.openPathAlt && {
        path: resolveOpenPath(feature.openPathAlt, betaOpts),
        label: feature.openPathAltLabel || '関連ページを開く',
        primary: false,
      },
      feature.openPathExtra && {
        path: resolveOpenPath(feature.openPathExtra, betaOpts),
        label: feature.openPathExtraLabel || '関連ページを開く',
        primary: false,
      },
    ].filter((action) => action && action.path);

    if (actions.length === 0) return null;
    return (
      <div className="hub-help-open-action">
        {actions.map((action) => (
          <button
            key={`${action.path}-${action.label}`}
            type="button"
            className={`hub-btn ${action.primary ? 'hub-btn-primary' : 'hub-btn-secondary'} hub-help-open-btn`}
            onClick={() => handleOpenFeature(action.path)}
          >
            {action.label}
          </button>
        ))}
      </div>
    );
  };

  let staggerIndex = 0;

  return (
    <HubHelpModalShell isOpen={isOpen} onClose={handleClose} ariaLabelledBy="hub-help-title">
      <div className="hub-help-modal-inner">
        <header className="hub-help-modal-header">
          <div>
            <h2 id="hub-help-title" className="hub-help-modal-title">ヘルプ・機能ガイド</h2>
            <p className="hub-help-modal-subtitle">
              困ったときの対処方法と、各機能の使い方を確認できます
            </p>
          </div>
          <button type="button" className="hub-help-modal-close" onClick={handleClose} aria-label="閉じる">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        {showContextSection && (
          <section className="hub-help-context" aria-label="この画面に関連するヘルプ">
            <h3 className="hub-help-context-title">この画面に関連するヘルプ</h3>
            <div className="hub-help-context-chips">
              {contextHelp.faqs.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="hub-help-context-chip hub-help-context-chip--faq"
                  onClick={() => {
                    const path = getFaqOpenPath(item, betaOpts);
                    if (path) handleOpenFeature(path);
                    else openFaq(item.id);
                  }}
                >
                  {item.title}
                </button>
              ))}
              {contextHelp.features.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="hub-help-context-chip hub-help-context-chip--feature"
                  onClick={() => {
                    const path = resolveFeatureOpenPath(item, betaOpts);
                    if (path) handleOpenFeature(path);
                    else openFeature(item.id);
                  }}
                >
                  {item.title}
                </button>
              ))}
            </div>
          </section>
        )}

        <div className="hub-help-toolbar">
          <div className="hub-help-tabs" role="tablist">
            {[
              { id: 'faq', label: 'トラブルシュート' },
              { id: 'map', label: '機能ガイド' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`hub-help-tab ${activeTab === tab.id ? 'hub-help-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="hub-help-search-row">
            <div className="hub-help-search-wrap">
              <svg className="hub-help-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="キーワードで検索…"
                className="hub-help-search-input"
                aria-label="ヘルプを検索"
              />
            </div>
            {activeTab === 'faq' && (
              <select
                value={faqCategory}
                onChange={(e) => setFaqCategory(e.target.value)}
                className="hub-help-category-select"
                aria-label="カテゴリで絞り込み"
              >
                <option value="">すべてのカテゴリ</option>
                {FAQ_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="hub-help-content" ref={contentRef}>
          {activeTab === 'map' && (
            <div className="hub-help-feature-list">
              {filteredCategories.length === 0 ? (
                <p className="hub-help-empty">該当する機能が見つかりません</p>
              ) : (
                filteredCategories.map((cat, catIdx) => (
                  <section
                    key={cat.id}
                    className="hub-help-category"
                    style={{ animationDelay: `${catIdx * 0.06}s` }}
                  >
                    <div className="hub-help-category-header">
                      <span className="hub-help-category-icon"><CategoryIcon type={cat.icon} /></span>
                      <h3 className="hub-help-category-title">{cat.title}</h3>
                      <span className="hub-help-category-count">{cat.features.length} 機能</span>
                    </div>
                    <div className="hub-help-accordion-list">
                      {cat.features.map((feature) => {
                        const isExpanded = expandedFeature === feature.id;
                        const delay = (staggerIndex += 1) * 0.04;
                        return (
                          <div
                            key={feature.id}
                            ref={(el) => setItemRef(`feature-${feature.id}`, el)}
                            className="hub-help-accordion-item"
                            style={{ animationDelay: `${delay}s` }}
                          >
                            <button
                              type="button"
                              className="hub-help-accordion-trigger"
                              aria-expanded={isExpanded}
                              onClick={() => setExpandedFeature(isExpanded ? null : feature.id)}
                            >
                              <div className="hub-help-accordion-trigger-text">
                                <div className="hub-help-accordion-title">{feature.title}</div>
                              </div>
                              <svg
                                className={`hub-help-accordion-chevron ${isExpanded ? 'hub-help-accordion-chevron--open' : ''}`}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                aria-hidden="true"
                              >
                                <path strokeLinecap="round" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {isExpanded && (
                              <div className="hub-help-accordion-body">
                                <p className="hub-help-feature-desc">{feature.description}</p>
                                {feature.steps?.length > 0 && (
                                  <ol className="hub-help-steps">
                                    {feature.steps.map((step, i) => (
                                      <li key={i} className="hub-help-step">
                                        <span className="hub-help-step-num">{i + 1}</span>
                                        <span className="hub-help-step-text">{step}</span>
                                      </li>
                                    ))}
                                  </ol>
                                )}
                                {renderFeatureOpenActions(feature)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ))
              )}
            </div>
          )}

          {activeTab === 'faq' && (
            <div className="hub-help-faq-list">
              {filteredFaq.length === 0 ? (
                <p className="hub-help-empty">該当するトラブルが見つかりません</p>
              ) : (
                filteredFaq.map((item, idx) => {
                  const isExpanded = expandedFaq === item.id;
                  const sev = SEVERITY_LABEL[item.severity] || SEVERITY_LABEL.low;
                  return (
                    <div
                      key={item.id}
                      ref={(el) => setItemRef(`faq-${item.id}`, el)}
                      className="hub-help-accordion-item hub-help-faq-item"
                      style={{ animationDelay: `${idx * 0.05}s` }}
                    >
                      <button
                        type="button"
                        className="hub-help-accordion-trigger hub-help-faq-trigger"
                        aria-expanded={isExpanded}
                        onClick={() => setExpandedFaq(isExpanded ? null : item.id)}
                      >
                        <span className={`hub-help-severity ${sev.className}`}>{sev.label}</span>
                        <span className="hub-help-faq-title">{item.title}</span>
                        <svg
                          className={`hub-help-accordion-chevron ${isExpanded ? 'hub-help-accordion-chevron--open' : ''}`}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" d="M19 9l-7 7-7-7" />
                        </svg>
                        <span className="hub-help-faq-category">{item.category}</span>
                      </button>
                      {isExpanded && (
                        <div className="hub-help-accordion-body hub-help-faq-body">
                          {item.symptoms?.length > 0 && (
                            <div className="hub-help-faq-section">
                              <h4 className="hub-help-faq-section-title">こんなとき</h4>
                              <ul className="hub-help-faq-bullets">
                                {item.symptoms.map((s, i) => <li key={i}>{s}</li>)}
                              </ul>
                            </div>
                          )}
                          {item.causes?.length > 0 && (
                            <div className="hub-help-faq-section">
                              <h4 className="hub-help-faq-section-title">よくある原因</h4>
                              <ul className="hub-help-faq-bullets">
                                {item.causes.map((c, i) => <li key={i}>{c}</li>)}
                              </ul>
                            </div>
                          )}
                          <div className="hub-help-faq-section">
                            <h4 className="hub-help-faq-section-title hub-help-faq-section-title--primary">対処方法</h4>
                            <ol className="hub-help-steps">
                              {item.solutions.map((sol, i) => (
                                <li key={i} className="hub-help-step">
                                  <span className="hub-help-step-num">{i + 1}</span>
                                  <span className="hub-help-step-text">{sol}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                          {item.relatedFeatures?.length > 0 && (
                            <div className="hub-help-related">
                              <h4 className="hub-help-faq-section-title">関連する機能ガイド</h4>
                              <div className="hub-help-related-chips">
                                {item.relatedFeatures.map((fid) => {
                                  const feat = getFeatureById(fid);
                                  if (!feat) return null;
                                  return (
                                    <button
                                      key={fid}
                                      type="button"
                                      className="hub-help-related-chip"
                                      onClick={() => {
                                        const path = resolveFeatureOpenPath(feat, betaOpts);
                                        if (path) handleOpenFeature(path);
                                        else openFeature(fid);
                                      }}
                                    >
                                      {feat.title}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {getFaqOpenPath(item, betaOpts) && (
                            <div className="hub-help-open-action">
                              <button
                                type="button"
                                className="hub-btn hub-btn-primary hub-help-open-btn"
                                onClick={() => handleOpenFeature(getFaqOpenPath(item, betaOpts))}
                              >
                                開く
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </HubHelpModalShell>
  );
}
